/** @jsx jsx */
import { Card, jsx } from '@front10/base-elements';
import BasePopover, { positionRight } from 'popover-reach-fr';
import {
  cloneElement,
  forwardRef,
  PropsWithChildren,
  ReactElement,
  RefObject,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  Fragment
} from 'react';

import { useOverlay } from '@react-aria/overlays';
import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';

interface Props {
  targetRef: RefObject<HTMLElement>;
  toggleElement: ReactElement;
  onClose?: () => void;
  onOpenPopup?: () => void;
  variant?: string;
  customAccessibility?: boolean;
  position?: boolean;
  portal?: boolean;
  tabIndex?: number;
  dataTestId?: string;
  useDelay?: boolean;
  useAsDialog?: boolean;
  dialogAriaLabel?: string;
  focusLock?: boolean;
}

export interface Dropdown {
  open: () => void;
  close: () => void;
  toggle: () => void;
  setOpen: (value: boolean) => void;
  isOpen: boolean;
}

export function useDropdown(
  props: Partial<
    Pick<Props, 'onClose' | 'onOpenPopup' | 'targetRef' | 'customAccessibility' | 'useDelay'>
  >
) {
  const [isOpen, setOpen] = useState(false);
  const privateTargetRef: RefObject<HTMLElement> = useRef(null);
  const targetRef = props.targetRef ?? privateTargetRef;
  const useDelay = props?.useDelay ?? true;

  const open = useCallback(() => setOpen(true), []);
  const onClose = props.onClose;
  const onOpenPopup = props.onOpenPopup;

  const close = useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [onClose]);

  const toggle = useCallback(() => {
    setOpen((oldIsOpen) => {
      if (oldIsOpen) {
        onClose?.();
      }
      onOpenPopup?.();
      return !oldIsOpen;
    });
  }, [onClose, onOpenPopup]);

  const timeoutId = useRef<number | undefined>(undefined);

  const onBlur = useCallback(() => {
    timeoutId.current = window.setTimeout(() => {
      close();
    });
  }, [close]);

  const onFocus = useCallback(() => {
    clearTimeout(timeoutId.current);
  }, []);

  const toggleElementProps = {
    onClick: useDelay
      ? () => {
          setTimeout(() => toggle(), 45); //small delay to avoid focus problems with other filters
        }
      : toggle,
    'aria-haspopup': props.customAccessibility ? null : 'true',
    'aria-expanded': isOpen,
    ref: targetRef
  };

  const containerProps = {
    onBlur,
    onFocus
  };

  return {
    open,
    close,
    toggle,
    isOpen,
    toggleElementProps,
    containerProps,
    targetRef,
    setOpen
  };
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const Dropdown = forwardRef<Dropdown, PropsWithChildren<Props>>(function Dropdown(
  props: PropsWithChildren<Props>,
  ref: ((instance: Dropdown) => void) | RefObject<Dropdown> | null
) {
  const {
    toggleElementProps,
    open,
    close,
    toggle,
    containerProps,
    isOpen,
    targetRef,
    setOpen
  } = useDropdown(props);

  useImperativeHandle(ref, () => ({
    open,
    close,
    toggle,
    setOpen,
    isOpen
  }));

  const toggleElement = cloneElement(props.toggleElement, toggleElementProps);
  const positionProps = props.position ? { position: positionRight } : null;
  const privateTargetRef: RefObject<HTMLElement> = useRef(null);
  const { portal = true } = props;

  const dialogRef = useRef(null);

  const { overlayProps } = useOverlay(
    {
      isOpen,
      onClose: close,
      // shouldCloseOnInteractOutside: (elem) => true,
      isDismissable: true // Allows closing via outside click/Escape
    },
    dialogRef
  );

  const { dialogProps } = useDialog(
    { role: props.useAsDialog ? 'dialog' : undefined, 'aria-label': props.dialogAriaLabel },
    dialogRef
  );

  const ParentComponent = props.focusLock ? FocusScope : Fragment;

  return (
    <div {...containerProps}>
      {toggleElement}
      <BasePopover
        portal={portal}
        sx={{
          display: isOpen ? undefined : 'none',
          variant: props.variant ?? 'layout.dropdown'
        }}
        tabIndex={props.tabIndex ? props.tabIndex : 0}
        ref={dialogRef}
        targetRef={portal ? targetRef : privateTargetRef}
        onKeyDown={(event) => event.keyCode === 27 && close()}
        {...overlayProps}
        {...dialogProps}
        {...positionProps}
      >
        <ParentComponent contain restoreFocus autoFocus>
          <Card data-testid={props.dataTestId}>{props.children}</Card>
        </ParentComponent>
      </BasePopover>
    </div>
  );
});
